import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { whileInUse, whileVisible } from 'shared/query/attention';
import {
  changeInitiativeState,
  createInitiativeWakeRule,
  createInitiativeDraft,
  deleteInitiativeWakeRule,
  deleteInitiative,
  getInitiative,
  getInitiativeActivity,
  getInitiativeConfiguration,
  getInitiativeConversation,
  getInitiativeWakeRules,
  getInitiativeWakeTrace,
  listSignals,
  listInitiatives,
  sendInitiativeMessage,
  startInitiative,
  updateInitiativeConfiguration,
  updateInitiativeLlm,
  updateInitiativeWakeRule,
  validateInitiativeWakeRule,
  wakeInitiativeNow,
} from './api';

export const initiativeKeys = {
  all: ['initiatives'],
  lists: () => ['initiatives', 'list'],
  list: (ownerAgentId = null) => ['initiatives', 'list', ownerAgentId],
  details: () => ['initiatives', 'detail'],
  detail: (id) => ['initiatives', 'detail', id],
  activity: (id) => ['initiatives', 'detail', id, 'activity'],
  wakeTrace: (id, turnId) => ['initiatives', 'detail', id, 'activity', turnId],
  conversation: (id) => ['initiatives', 'detail', id, 'conversation'],
  configuration: (id) => ['initiatives', 'detail', id, 'configuration'],
  wakeRules: (id) => ['initiatives', 'detail', id, 'wake-rules'],
  signalDefinitions: ['signals', 'active'],
};

// Agents poll the projection and activity feed. Poll fast while an Initiative can
// change on its own (including live wake traces), slowly while it waits on
// people, and not at all once it is closed.
const LIVE_STATUSES = new Set(['running', 'queued']);
const SETTLED_STATUSES = new Set(['succeeded', 'failed', 'cancelled']);
const LIVE_INTERVAL = 5_000;
const OPEN_INTERVAL = 15_000;
const LIST_LIVE_INTERVAL = 10_000;
const LIST_INTERVAL = 30_000;
const ACTIVITY_LIVE_INTERVAL = 1_000;

export const useInitiatives = (ownerAgentId = null) =>
  useQuery({
    queryKey: initiativeKeys.list(ownerAgentId),
    queryFn: () => listInitiatives(ownerAgentId),
    staleTime: 5_000,
    refetchInterval: (query) => {
      const items = query?.state?.data?.items || [];
      const live = items.some((item) => LIVE_STATUSES.has(item?.status));
      return live ? whileVisible(LIST_LIVE_INTERVAL) : whileInUse(LIST_INTERVAL);
    },
    refetchIntervalInBackground: true,
  });

export const useInitiative = (initiativeId) =>
  useQuery({
    queryKey: initiativeKeys.detail(initiativeId),
    queryFn: () => getInitiative(initiativeId),
    enabled: Boolean(initiativeId),
    staleTime: 5_000,
    refetchInterval: (query) => {
      const status = query?.state?.data?.status;
      if (SETTLED_STATUSES.has(status)) return false;
      return LIVE_STATUSES.has(status) ? whileVisible(LIVE_INTERVAL) : whileInUse(OPEN_INTERVAL);
    },
    refetchIntervalInBackground: true,
  });

export const useInitiativeActivity = (initiativeId, { live = false, settled = false } = {}) =>
  useQuery({
    queryKey: initiativeKeys.activity(initiativeId),
    queryFn: () => getInitiativeActivity(initiativeId),
    enabled: Boolean(initiativeId),
    staleTime: 5_000,
    refetchInterval: (query) => {
      if (settled) return false;
      const wakes = query?.state?.data?.wakes || [];
      const hasLiveWake = wakes.some(
        (wake) => wake?.live || wake?.status === 'running'
      );
      if (live || hasLiveWake) return whileVisible(ACTIVITY_LIVE_INTERVAL);
      return whileInUse(OPEN_INTERVAL);
    },
    refetchIntervalInBackground: true,
  });

export const useInitiativeWakeTrace = (initiativeId, turnId, { enabled = true } = {}) =>
  useQuery({
    queryKey: initiativeKeys.wakeTrace(initiativeId, turnId),
    queryFn: () => getInitiativeWakeTrace(initiativeId, turnId),
    enabled: Boolean(initiativeId && turnId && enabled),
    refetchInterval: (query) => {
      const status = query?.state?.data?.status;
      if (status === 'running') return whileVisible(ACTIVITY_LIVE_INTERVAL);
      return false;
    },
    refetchIntervalInBackground: true,
  });

export const useInitiativeConversation = (initiativeId, { live = false, settled = false } = {}) =>
  useQuery({
    queryKey: initiativeKeys.conversation(initiativeId),
    queryFn: () => getInitiativeConversation(initiativeId),
    enabled: Boolean(initiativeId),
    staleTime: 5_000,
    refetchInterval: () => (settled ? false : live ? whileVisible(ACTIVITY_LIVE_INTERVAL) : whileInUse(OPEN_INTERVAL)),
    refetchIntervalInBackground: true,
  });

export const useInitiativeConfiguration = (initiativeId, { enabled = true } = {}) =>
  useQuery({
    queryKey: initiativeKeys.configuration(initiativeId),
    queryFn: () => getInitiativeConfiguration(initiativeId),
    enabled: Boolean(initiativeId) && enabled,
    staleTime: 30_000,
  });

export const useInitiativeWakeRules = (initiativeId, { settled = false } = {}) =>
  useQuery({
    queryKey: initiativeKeys.wakeRules(initiativeId),
    queryFn: () => getInitiativeWakeRules(initiativeId),
    enabled: Boolean(initiativeId),
    staleTime: 15_000,
    refetchInterval: settled ? false : OPEN_INTERVAL,
  });

export const usePublishedSignalDefinitions = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: initiativeKeys.signalDefinitions,
    queryFn: listSignals,
    enabled,
    staleTime: 30_000,
  });

export const useCreateInitiative = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createInitiativeDraft(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: initiativeKeys.lists() });
    },
  });
};

export const useInitiativeMutation = (initiativeId) => {
  const queryClient = useQueryClient();
  const showExecutionError = (error) => toast.error(
    error?.message || 'The initiative could not start. Please try again.',
    { autoClose: false },
  );
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: initiativeKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: initiativeKeys.detail(initiativeId) }),
    ]);
  };
  const clearDeletedAgent = async () => {
    await queryClient.cancelQueries({ queryKey: initiativeKeys.detail(initiativeId) });
    queryClient.removeQueries({ queryKey: initiativeKeys.detail(initiativeId) });
    await queryClient.invalidateQueries({ queryKey: initiativeKeys.lists() });
  };
  return {
    start: useMutation({ mutationFn: () => startInitiative(initiativeId), onSuccess: invalidate, onError: showExecutionError }),
    input: useMutation({
      mutationFn: ({ message, metadata, files, initiativeAskId, choice }) =>
        sendInitiativeMessage(initiativeId, message, metadata, files, initiativeAskId, choice),
      onSuccess: invalidate,
    }),
    lifecycle: useMutation({
      mutationFn: ({ action, reason }) => changeInitiativeState(initiativeId, action, reason),
      onSuccess: invalidate,
      onError: showExecutionError,
    }),
    remove: useMutation({
      mutationFn: () => deleteInitiative(initiativeId),
      onSuccess: clearDeletedAgent,
    }),
    wakeNow: useMutation({ mutationFn: () => wakeInitiativeNow(initiativeId), onSuccess: invalidate, onError: showExecutionError }),
    configuration: useMutation({
      mutationFn: (payload) => updateInitiativeConfiguration(initiativeId, payload),
      onSuccess: invalidate,
    }),
    llm: useMutation({
      mutationFn: (payload) => updateInitiativeLlm(initiativeId, payload),
      onSuccess: invalidate,
    }),
    createWakeRule: useMutation({
      mutationFn: (payload) => createInitiativeWakeRule(initiativeId, payload),
      onSuccess: invalidate,
    }),
    updateWakeRule: useMutation({
      mutationFn: ({ ruleId, payload }) => updateInitiativeWakeRule(initiativeId, ruleId, payload),
      onSuccess: invalidate,
    }),
    deleteWakeRule: useMutation({
      mutationFn: (ruleId) => deleteInitiativeWakeRule(initiativeId, ruleId),
      onSuccess: invalidate,
    }),
    validateWakeRule: useMutation({
      mutationFn: (payload) => validateInitiativeWakeRule(initiativeId, payload),
    }),
  };
};
